// src/components/pages/Settings.jsx
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Badge from "../ui/Badge";
import Field, { PasswordField } from "../ui/Field";
import Icon from "../ui/Icon";
import Spinner, { Toast } from "../ui/Spinner";

import axiosInstance from "../../utils/AxiosInstance";
import { logoutUser } from "../../redux/actions/userActions";
import { getMyTransactions, formatUsd } from "../../services/resultsService";

/**
 * One settings page, six tabs:
 *
 *   1. Account       — name + email (read-only display, account info)
 *   2. Password      — 3-step in-app password change flow
 *                       a. Request code (POST /site/user/forgot-password)
 *                       b. Confirm 6-digit code
 *                       c. Set new password
 *   3. Subscription  — current sub, cancel, update card
 *   4. Purchases     — transaction history (GET /site/payment/my-transactions)
 *   5. Notifications — email-on-results preference
 *                       (PUT /site/user/notification-preferences)
 *   6. Support       — contact form (POST /site/contact-message)
 *
 * All endpoints exactly match what the original Dashboard.jsx used —
 * which means existing backend continues to work without change.
 */
const TABS = [
  { id: "account",       label: "Account",       icon: "ticket" },
  { id: "password",      label: "Password",      icon: "shield-check" },
  { id: "subscription",  label: "Subscription",  icon: "credit-card" },
  { id: "purchases",     label: "Purchases",     icon: "receipt" },
  { id: "notifications", label: "Notifications", icon: "bell" },
  { id: "support",       label: "Support",       icon: "send" },
];

export default function Settings() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("account");
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState({ open: false, tone: "success", message: "" });

  const showToast = (message, tone = "success") => {
    setToast({ open: true, tone, message });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 3500);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/site/user/logged-user");
        if (res.data?.id) setUser(res.data);
      } catch { /* protected route */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleLogout = async () => {
    try { await axiosInstance.post("/security/auth/logout"); } catch {}
    dispatch(logoutUser());
    window.dispatchEvent(new CustomEvent("auth:changed", {
      detail: { authenticated: false, source: "settings-logout" },
    }));
    navigate("/", { replace: true });
  };

  return (
    <AppShell>
      <Toast open={toast.open} tone={toast.tone} message={toast.message} />

      <div className="px-4 sm:px-6 py-6 max-w-[860px] mx-auto">

        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 text-[12px] mb-3 hover:underline"
          style={{ color: "var(--txt-2)" }}
        >
          <Icon name="arrow-left" size={13} />
          Back to dashboard
        </button>

        <header className="mb-6">
          <h1 className="font-semibold mb-1" style={{ fontSize: 26, color: "var(--txt)" }}>
            Settings
          </h1>
          <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>
            Manage your account, password, subscription, and support.
          </p>
        </header>

        <div className="flex flex-wrap gap-1.5 mb-5 p-1 rounded-full"
             style={{ background: "var(--surf-2)", border: "1px solid var(--border)" }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors"
              style={{
                background: activeTab === t.id ? "var(--surf)" : "transparent",
                color: activeTab === t.id ? "var(--txt)" : "var(--txt-2)",
                boxShadow: activeTab === t.id ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
              }}
            >
              <Icon name={t.icon} size={13} />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Card>
            <p className="text-[13px] text-center" style={{ color: "var(--txt-3)" }}>
              Loading…
            </p>
          </Card>
        ) : (
          <>
            {activeTab === "account"       && <AccountTab user={user} onLogout={handleLogout} />}
            {activeTab === "password"      && <PasswordTab user={user} showToast={showToast} />}
            {activeTab === "subscription"  && <SubscriptionTab user={user} showToast={showToast} />}
            {activeTab === "purchases"     && <PurchasesTab />}
            {activeTab === "notifications" && <NotificationsTab user={user} showToast={showToast} />}
            {activeTab === "support"       && <SupportTab user={user} showToast={showToast} />}
          </>
        )}
      </div>
    </AppShell>
  );
}

/* ============================================================
   Account tab
   ============================================================ */
function AccountTab({ user, onLogout }) {
  return (
    <>
      <Card className="mb-3">
        <div className="lg-label mb-3">Profile</div>
        <Row label="Name"  value={user?.name  || "—"} />
        <Row label="Email" value={user?.email || "—"} />
        <Row label="Credits" value={`${user?.account?.credit ?? 0}`} last />
      </Card>

      <Card>
        <div className="lg-label mb-3">Session</div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[13px]" style={{ color: "var(--txt)" }}>Log out</div>
            <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
              End your current session on this device.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout}>
            Log out
          </Button>
        </div>
      </Card>
    </>
  );
}

function Row({ label, value, last = false }) {
  return (
    <div
      className="flex items-center justify-between py-2.5"
      style={{ borderBottom: last ? "none" : "1px solid var(--border)" }}
    >
      <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{label}</span>
      <span className="text-[13px] font-medium" style={{ color: "var(--txt)" }}>{value}</span>
    </div>
  );
}

/* ============================================================
   Password tab — in-app version of /reset-password
   ============================================================ */
function PasswordTab({ user, showToast }) {
  // Step 1: request code → Step 2: confirm code → Step 3: set new password
  const [step, setStep]           = useState(1);
  const [code, setCode]           = useState("");
  const [newPw, setNewPw]         = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState("");
  const [msg, setMsg]             = useState("");

  const email = (user?.email || "").trim().toLowerCase();

  const requestCode = async () => {
    if (!email) {
      setErr("No email associated with this account.");
      return;
    }
    setLoading(true); setErr(""); setMsg("");
    try {
      await axiosInstance.post("/site/user/forgot-password", { email });
      setStep(2);
      setMsg("We sent a 6-digit code to your email. Enter it below to continue.");
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "Couldn't send the code. Try again.");
    } finally { setLoading(false); }
  };

  const confirmCode = async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      setErr("Enter the 6-digit code from your email.");
      return;
    }
    setLoading(true); setErr(""); setMsg("");
    try {
      await axiosInstance.post(
        `/site/user/confirm-code-change-password/${encodeURIComponent(email)}`,
        { code: Number(code.trim()) }
      );
      setStep(3);
      setMsg("Code confirmed. Choose your new password.");
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "That code didn't work. Try again or resend.");
    } finally { setLoading(false); }
  };

  const changePassword = async () => {
    if (newPw.length < 8) {
      setErr("Your new password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setErr("New password and confirmation don't match.");
      return;
    }
    setLoading(true); setErr(""); setMsg("");
    try {
      await axiosInstance.put(
        `/site/user/change-password/${encodeURIComponent(email)}/${encodeURIComponent(code.trim())}`,
        { password: newPw }
      );
      showToast("Password updated successfully.", "success");
      setStep(1);
      setCode(""); setNewPw(""); setConfirmPw("");
      setMsg("Your password has been changed. Use the new one next time you log in.");
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "Couldn't update your password. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <Card>
      <div className="lg-label mb-1">Change password</div>
      <p className="text-[12px] mb-4" style={{ color: "var(--txt-2)" }}>
        For your security, we send a one-time code to your email before
        accepting a new password.
      </p>

      {err && (
        <div className="mb-3 p-3 rounded-[10px] text-[12px] flex items-start gap-2"
             style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}>
          <Icon name="alert-circle" size={14} style={{ marginTop: 1 }} />
          <span>{err}</span>
        </div>
      )}
      {msg && (
        <div className="mb-3 p-3 rounded-[10px] text-[12px] flex items-start gap-2"
             style={{ background: "var(--teal-dim)", border: "1px solid var(--teal-border)", color: "var(--teal)" }}>
          <Icon name="circle-check" size={14} style={{ marginTop: 1 }} />
          <span>{msg}</span>
        </div>
      )}

      <Row label="Account email" value={email || "—"} last />

      <div className="flex items-center gap-2 mt-2 mb-4">
        <StepDot active={step >= 1} done={step > 1} num={1} label="Request" />
        <StepLine done={step > 1} />
        <StepDot active={step >= 2} done={step > 2} num={2} label="Confirm" />
        <StepLine done={step > 2} />
        <StepDot active={step >= 3} done={false}    num={3} label="Set new"  />
      </div>

      {step === 1 && (
        <Button onClick={requestCode} loading={loading} full>
          {!loading && <Icon name="send" size={14} />}
          {loading ? "Sending..." : "Send code to my email"}
        </Button>
      )}

      {step === 2 && (
        <>
          <Field
            label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            inputMode="numeric"
            maxLength={6}
          />
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={requestCode} loading={loading} className="!w-auto">
              Resend
            </Button>
            <Button onClick={confirmCode} loading={loading} full>
              Confirm code
            </Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <PasswordField
            label="New password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            hint="At least 8 characters."
          />
          <PasswordField
            label="Confirm new password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />
          <Button onClick={changePassword} loading={loading} full>
            Update password
          </Button>
        </>
      )}
    </Card>
  );
}

function StepDot({ active, done, num, label }) {
  return (
    <div className="flex flex-col items-center text-center" style={{ minWidth: 56 }}>
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold mb-1"
        style={{
          background: done ? "var(--teal)" : active ? "var(--gold)" : "var(--surf-2)",
          color: done || active ? "#23364C" : "var(--txt-3)",
          border: `1px solid ${done ? "var(--teal)" : active ? "var(--gold)" : "var(--border)"}`,
        }}
      >
        {done ? <Icon name="check" size={14} /> : num}
      </div>
      <span className="text-[10px]" style={{ color: active || done ? "var(--txt)" : "var(--txt-3)" }}>
        {label}
      </span>
    </div>
  );
}

function StepLine({ done }) {
  return (
    <div
      className="flex-1 h-[2px] mt-3"
      style={{ background: done ? "var(--teal)" : "var(--border)" }}
    />
  );
}

/* ============================================================
   Subscription tab — view, cancel, update payment method
   ============================================================ */
function SubscriptionTab({ user, showToast }) {
  const [sub, setSub]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");

  // Cancel flow
  const [showCancel, setShowCancel]       = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Update payment method flow
  const [updatingPM, setUpdatingPM] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const res = await axiosInstance.get("/site/subscription/me");
      const d = res?.data;
      setSub(d && typeof d === "object" && d.id ? d : null);
    } catch (e) {
      if (e?.response?.status === 404) {
        setSub(null);
      } else {
        setErr("Couldn't load your subscription details.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCancel = async () => {
    setCancelLoading(true); setErr("");
    try {
      const res = await axiosInstance.post("/site/subscription/cancel");
      if (res?.data) setSub(res.data);
      await load();
      showToast("Subscription cancelled. You'll keep access until the end of the period.", "success");
      setShowCancel(false);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "Couldn't cancel right now.");
    } finally { setCancelLoading(false); }
  };

  const handleUpdatePM = async () => {
    if (!sub?.paymentMethod) return;
    setUpdatingPM(true); setErr("");
    try {
      if (sub.paymentMethod === "PAYPAL") {
        const url = (process.env.REACT_APP_PAYPAL_AUTOPAY_URL || "").trim();
        const fallback = window?.location?.hostname?.match(/^(localhost|127\.0\.0\.1)$/)
          ? "https://www.sandbox.paypal.com/myaccount/autopay/connect/"
          : "";
        const open = url || fallback;
        if (!open) {
          setErr("PayPal management link not configured (REACT_APP_PAYPAL_AUTOPAY_URL).");
          return;
        }
        window.open(open, "_blank", "noopener,noreferrer");
        return;
      }
      if (sub.paymentMethod === "STRIPE") {
        const res = await axiosInstance.post("/site/subscription/stripe/update-payment-method");
        const u = res?.data?.url;
        if (!u) {
          setErr("Stripe didn't return an update URL. Try again in a moment.");
          return;
        }
        window.location.assign(u);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "Couldn't start payment method update.");
    } finally { setUpdatingPM(false); }
  };

  if (loading) {
    return (
      <Card>
        <p className="text-[13px] text-center" style={{ color: "var(--txt-3)" }}>
          Loading subscription…
        </p>
      </Card>
    );
  }

  if (!sub) {
    return (
      <Card className="text-center py-10">
        <Icon name="credit-card" size={32} style={{ color: "var(--txt-3)" }} />
        <h2 className="font-medium mt-3 mb-1" style={{ color: "var(--txt)" }}>
          No active subscription
        </h2>
        <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
          You're on the pay-as-you-go plan. Start a monthly bundle to get
          30 credits each month.
        </p>
        <Button onClick={() => window.location.assign("/plans")}>
          See plans
          <Icon name="arrow-right" size={14} />
        </Button>
      </Card>
    );
  }

  const statusLabel = String(sub.status || "").replaceAll("_", " ");
  const statusTone =
    sub.status === "ACTIVE"   ? { bg: "var(--teal-dim)", color: "var(--teal)", border: "var(--teal-border)" } :
    sub.status === "PAST_DUE" ? { bg: "var(--gold-dim)", color: "var(--gold)", border: "var(--gold-border)" } :
                                { bg: "var(--surf-2)",  color: "var(--txt-3)", border: "var(--border)"       };

  const nextPaymentDate = sub.nextPaymentDate || sub.nextBillingDate;
  const isCancelled = sub.status === "CANCELED" || sub.status === "CANCELLED";

  return (
    <>
      {err && (
        <div className="mb-3 p-3 rounded-[10px] text-[12px] flex items-start gap-2"
             style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}>
          <Icon name="alert-circle" size={14} style={{ marginTop: 1 }} />
          <span>{err}</span>
        </div>
      )}

      <Card className="mb-3">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="lg-label mb-1">Your subscription</div>
            <div className="font-semibold" style={{ fontSize: 18, color: "var(--txt)" }}>
              {sub.plan || "Monthly"}
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
            style={{ background: statusTone.bg, color: statusTone.color, border: `1px solid ${statusTone.border}` }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusTone.color }} />
            {statusLabel}
          </span>
        </div>

        <Row label="Payment method" value={sub.paymentMethod || "—"} />
        {nextPaymentDate && (
          <Row label={isCancelled ? "Access ends" : "Next renewal"}
               value={new Date(nextPaymentDate).toLocaleDateString(undefined, {
                 year: "numeric", month: "short", day: "numeric",
               })} />
        )}
        <Row label="Started"
             value={sub.dateCreate ? new Date(sub.dateCreate).toLocaleDateString() : "—"}
             last />
      </Card>

      <div className="grid sm:grid-cols-2 gap-3">
        <Card variant="soft">
          <div className="font-medium text-[14px] mb-1" style={{ color: "var(--txt)" }}>
            Update payment method
          </div>
          <p className="text-[12px] mb-3" style={{ color: "var(--txt-2)" }}>
            Change the card or PayPal account used for future renewals.
          </p>
          <Button
            variant="ghost"
            size="sm"
            full
            loading={updatingPM}
            onClick={handleUpdatePM}
            disabled={isCancelled}
          >
            {!updatingPM && <Icon name="credit-card" size={14} />}
            {sub.paymentMethod === "PAYPAL" ? "Open PayPal" : "Update card"}
          </Button>
        </Card>

        <Card variant="soft">
          <div className="font-medium text-[14px] mb-1" style={{ color: "var(--txt)" }}>
            {isCancelled ? "Already cancelled" : "Cancel subscription"}
          </div>
          <p className="text-[12px] mb-3" style={{ color: "var(--txt-2)" }}>
            {isCancelled
              ? "You'll keep access until the end of your current period."
              : "Stop future renewals. You keep access until the period ends."}
          </p>
          <Button
            variant="danger"
            size="sm"
            full
            disabled={isCancelled}
            onClick={() => setShowCancel(true)}
          >
            {isCancelled ? "Cancelled" : "Cancel subscription"}
          </Button>
        </Card>
      </div>

      {/* Cancel confirmation modal */}
      {showCancel && (
        <div
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center px-4 py-8"
          style={{ background: "rgba(35,54,76,0.55)", backdropFilter: "blur(8px)" }}
          onClick={() => !cancelLoading && setShowCancel(false)}
        >
          <Card
            className="w-full max-w-[420px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: "var(--red-dim)",
                  color: "var(--red)",
                  border: "1px solid var(--red-border)",
                }}
              >
                <Icon name="alert-circle" size={20} />
              </div>
              <div>
                <div className="font-semibold mb-1" style={{ color: "var(--txt)" }}>
                  Cancel your subscription?
                </div>
                <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                  Your subscription stays active until the end of the
                  current billing period. After that you'll lose access
                  to monthly credits.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="ghost" full onClick={() => setShowCancel(false)} disabled={cancelLoading}>
                Keep subscription
              </Button>
              <Button variant="danger" full onClick={handleCancel} loading={cancelLoading}>
                Yes, cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

/* ============================================================
   Purchases tab — transaction history
   ============================================================ */
function statusBadge(status) {
  switch (status) {
    case "PAID_OUT":   return <Badge variant="teal">Paid out</Badge>;
    case "PROCESSING": return <Badge variant="gold">Processing</Badge>;
    case "ERROR":      return <Badge variant="red">Error</Badge>;
    case "REFUNDED":   return <Badge variant="blue">Refunded</Badge>;
    default:           return <Badge variant="neutral">{status || "—"}</Badge>;
  }
}

function PurchasesTab() {
  const [txs, setTxs]         = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getMyTransactions().catch(() => []);
      if (!cancelled) {
        setTxs([...data].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card>
        <div className="flex items-center gap-2 py-2">
          <Spinner size={14} />
          <span className="text-[13px]" style={{ color: "var(--txt-3)" }}>Loading purchases…</span>
        </div>
      </Card>
    );
  }

  if (txs.length === 0) {
    return (
      <Card className="text-center py-10">
        <Icon name="receipt" size={32} style={{ color: "var(--txt-3)" }} />
        <h2 className="font-medium mt-3 mb-1" style={{ color: "var(--txt)" }}>
          No purchases yet
        </h2>
        <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>
          Your credit and subscription purchases will show up here.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="lg-label mb-3">Purchase history</div>
      {txs.map((t, idx) => (
        <div
          key={t.id ?? idx}
          className="flex items-center justify-between py-3"
          style={{ borderBottom: idx === txs.length - 1 ? "none" : "1px solid var(--border)" }}
        >
          <div>
            <div className="text-[13px] font-medium" style={{ color: "var(--txt)" }}>
              {(t.transactionType || "Transaction").toString().replaceAll("_", " ")}
              {t.creditPlan ? ` — ${String(t.creditPlan).replaceAll("_", " ")}` : ""}
            </div>
            <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>
              {t.date ? new Date(t.date).toLocaleDateString(undefined, {
                year: "numeric", month: "short", day: "numeric",
              }) : "—"}
              {t.paymentMethod ? ` · ${t.paymentMethod}` : ""}
            </div>
          </div>
          <div className="text-right flex items-center gap-2">
            <span className="text-[13px] font-semibold" style={{ color: "var(--txt)" }}>
              {formatUsd(t.amount)}
            </span>
            {statusBadge(t.status)}
          </div>
        </div>
      ))}
    </Card>
  );
}

/* ============================================================
   Notifications tab — email-on-results preference
   ============================================================ */
function NotificationsTab({ user, showToast }) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    setEnabled(Boolean(user?.emailOnResultsEnabled));
    setLoading(false);
  }, [user]);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      await axiosInstance.put("/site/user/notification-preferences", {
        emailOnResultsEnabled: next,
      });
      setEnabled(next);
      showToast(
        next ? "You'll get an email when your results are in." : "Result emails turned off.",
        "success"
      );
    } catch (e) {
      showToast(
        e?.response?.data?.message || e?.response?.data?.error || "Couldn't update your preference.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <p className="text-[13px] text-center" style={{ color: "var(--txt-3)" }}>
          Loading…
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="lg-label mb-3">Email notifications</div>
      <div className="flex items-center justify-between">
        <div className="pr-4">
          <div className="text-[13px] font-medium" style={{ color: "var(--txt)" }}>
            Email me when results are in
          </div>
          <p className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
            Get an email as soon as a drawing you played has been scored,
            win or not.
          </p>
        </div>
        <label className="inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            checked={enabled}
            disabled={saving}
            onChange={toggle}
            className="sr-only peer"
          />
          <div
            className="w-10 h-6 rounded-full transition-colors relative"
            style={{ background: enabled ? "var(--gold)" : "var(--surf-2)", border: "1px solid var(--border-2)" }}
          >
            <div
              className="absolute top-[2px] w-[18px] h-[18px] rounded-full transition-transform"
              style={{
                background: "#fff",
                transform: enabled ? "translateX(19px)" : "translateX(2px)",
              }}
            />
          </div>
        </label>
      </div>
    </Card>
  );
}

/* ============================================================
   Support tab — contact message
   ============================================================ */
function SupportTab({ user, showToast }) {
  const [subject, setSubject]   = useState("");
  const [message, setMessage]   = useState("");
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!message.trim()) {
      setErr("Please write a message.");
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post(
        "/site/contact-message",
        {
          name: user?.name || "Account user",
          email: user?.email || "",
          subject: subject.trim() || "Support request from settings",
          message: message.trim(),
          type: "GENERAL",
          userId: user?.id,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      showToast("Message sent. We'll reply by email.", "success");
      setSubject(""); setMessage("");
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || "Couldn't send your message right now.");
    } finally { setLoading(false); }
  };

  return (
    <Card>
      <div className="lg-label mb-1">Message us</div>
      <p className="text-[12px] mb-4" style={{ color: "var(--txt-2)" }}>
        We'll reply by email — typically within one business day.
      </p>

      {err && (
        <div className="mb-3 p-3 rounded-[10px] text-[12px] flex items-start gap-2"
             style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}>
          <Icon name="alert-circle" size={14} style={{ marginTop: 1 }} />
          <span>{err}</span>
        </div>
      )}

      <form onSubmit={submit}>
        <Field
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="What's this about?"
        />

        <div className="mb-4">
          <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="How can we help?"
            required
            rows={6}
            className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-[16px] border placeholder:text-[var(--txt-3)] resize-y"
            style={{
              background: "var(--surf-2)",
              color: "var(--txt)",
              borderColor: "var(--border-2)",
            }}
            onFocus={(e) => (e.target.style.borderColor = "var(--gold)")}
            onBlur={(e) => (e.target.style.borderColor = "var(--border-2)")}
          />
        </div>

        <Button type="submit" loading={loading} full>
          {!loading && <Icon name="send" size={14} />}
          {loading ? "Sending..." : "Send message"}
        </Button>
      </form>
    </Card>
  );
}
